import "./buzonAnonimoAdmin.scss";
import { useState, useEffect, useContext } from "react";
import { collection, getDocs, query, orderBy, doc, getDoc, updateDoc, deleteDoc, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { AuthContext } from "../../context/AuthContext";
import { fetchTiendasAdmin } from "./fetchTiendas";

/**
 * Componente para la administración de mensajes anónimos.
 * Permite ver, marcar como leídos y eliminar mensajes anónimos.
 *
 * @returns {JSX.Element} Elemento JSX que representa el administrador de buzón anónimo.
 */
const BuzonAnonimoAdmin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTiendas, setLoadingTiendas] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [expandedMessage, setExpandedMessage] = useState(null);
  const [tiendas, setTiendas] = useState([]);
  const [error, setError] = useState(null);
  const [showReadMessages, setShowReadMessages] = useState(true);

  // Contexto de autenticación
  const { currentUser, currentService, adminData } = useContext(AuthContext);

  // Obtener tiendas asociadas al administrador
  useEffect(() => {
    const getTiendas = async () => {
      if (!currentService || !adminData || !adminData.tiendas) {
        console.log("Datos insuficientes para obtener tiendas:", { 
          currentService: currentService || "no disponible", 
          adminData: adminData ? "disponible" : "no disponible",
          tiendas: adminData?.tiendas ? `${adminData.tiendas.length} tiendas` : "no disponibles"
        });
        
        setLoadingTiendas(false);
        setError("No se encontraron servicios asignados");
        return;
      }
      
      try {
        console.log("Obteniendo tiendas para el administrador:", {
          serviceId: currentService.id,
          adminTiendas: adminData.tiendas
        });
        
        const tiendasList = await fetchTiendasAdmin(currentService.id, adminData.tiendas);
        console.log("Tiendas obtenidas:", tiendasList);
        
        if (tiendasList.length === 0) {
          setError("No se encontraron servicios asignados a tu cuenta");
        } else {
          setTiendas(tiendasList);
          setError(null);
        }
      } catch (error) {
        console.error("Error al obtener tiendas del administrador:", error);
        setError("Error al cargar los servicios asignados");
      } finally {
        setLoadingTiendas(false);
      }
    };

    getTiendas();
  }, [currentService, adminData]);

  // Cargar mensajes cuando cambian las tiendas
  useEffect(() => {
    const fetchMessages = async () => {
      if (!currentService || tiendas.length === 0) {
        setLoading(false);
        return;
      }

      try {
        // Obtener los identificadores de tienda para filtrar
        const tiendasIdentificadores = tiendas.map(tienda => tienda.numero);
        console.log("Cargando mensajes para los servicios:", tiendasIdentificadores);
        
        // Verificar si hay algún identificador válido
        if (tiendasIdentificadores.length === 0) {
          console.log("No hay identificadores de servicio válidos para filtrar");
          setMessages([]);
          setLoading(false);
          return;
        }
        
        // Intentar cargar todos los mensajes primero
        const mensajesRef = collection(db, "servicios", currentService.id, "mensajesAnonimos");
        const mensajesSnapshot = await getDocs(mensajesRef);
        
        console.log(`Total de mensajes encontrados: ${mensajesSnapshot.size}`);
        
        if (mensajesSnapshot.empty) {
          console.log("No hay mensajes en la colección");
          setMessages([]);
          setLoading(false);
          return;
        }
        
        // Filtrar manualmente los mensajes por identificadores de tienda
        const mensajesFiltrados = mensajesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(mensaje => {
            // Verificar si el mensaje tiene un identificador de tienda
            if (mensaje.tiendaNumero === undefined || mensaje.tiendaNumero === null) {
              console.log(`Mensaje ${mensaje.id} no tiene tiendaNumero`);
              return false;
            }
            
            // Convertir a string para comparación segura
            const tiendaNumeroStr = String(mensaje.tiendaNumero);
            
            // Verificar si el identificador está en nuestras tiendas
            // usando some para buscar coincidencias en el array
            const tieneAcceso = tiendasIdentificadores.some(id => {
              // Convertir el id de la tienda a string para comparación segura
              const idStr = String(id);
              return idStr === tiendaNumeroStr;
            });
            
            console.log(`Mensaje ${mensaje.id} - tiendaNumero: ${tiendaNumeroStr}, ¿tiene acceso?: ${tieneAcceso}`);
            
            return tieneAcceso;
          });
        
        console.log("Mensajes filtrados:", mensajesFiltrados);
        
        // Ordenar los mensajes por fecha (del más reciente al más antiguo)
        const mensajesOrdenados = mensajesFiltrados.sort((a, b) => {
          if (!a.fecha || !b.fecha) return 0;
          
          const fechaA = a.fecha.toDate ? a.fecha.toDate() : new Date(a.fecha);
          const fechaB = b.fecha.toDate ? b.fecha.toDate() : new Date(b.fecha);
          
          return fechaB - fechaA;
        });
        
        setMessages(mensajesOrdenados);
      } catch (error) {
        console.error("Error al cargar los mensajes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [currentService, tiendas]);

  // Marcar mensaje como leído/no leído
  const toggleReadStatus = async (id, currentStatus) => {
    if (!currentService?.id) return;
    
    try {
      console.log(`Marcando mensaje ${id} como ${currentStatus ? 'no leído' : 'leído'}`);
      
      const messageRef = doc(db, "servicios", currentService.id, "mensajesAnonimos", id);
      await updateDoc(messageRef, {
        leido: !currentStatus
      });
      
      // Actualizar estado local
      setMessages(messages.map(message => 
        message.id === id ? { ...message, leido: !currentStatus } : message
      ));
    } catch (error) {
      console.error("Error al actualizar el estado del mensaje:", error);
    }
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (id) => {
    setMessageToDelete(id);
    setOpenDialog(true);
  };

  // Cerrar diálogo
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMessageToDelete(null);
  };

  // Eliminar mensaje
  const deleteMessage = async () => {
    if (!messageToDelete || !currentService?.id) return;
    
    try {
      await deleteDoc(doc(db, "servicios", currentService.id, "mensajesAnonimos", messageToDelete));
      setMessages(messages.filter(message => message.id !== messageToDelete));
      handleCloseDialog();
    } catch (error) {
      console.error("Error al eliminar el mensaje:", error);
    }
  };

  // Expandir/colapsar mensaje
  const toggleExpandMessage = (id) => {
    if (expandedMessage === id) {
      setExpandedMessage(null);
    } else {
      setExpandedMessage(id);
    }
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return "Fecha desconocida";
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
    } catch (error) {
      console.error("Error al formatear la fecha:", error);
      return "Fecha inválida";
    }
  };

  // Obtener información del servicio
  const getServicioInfo = (tiendaNumero) => {
    if (!tiendaNumero) return null;
    
    // Buscar por número, convertido a string para comparación segura
    const tiendaNumeroStr = String(tiendaNumero);
    const tienda = tiendas.find(t => String(t.numero) === tiendaNumeroStr);
    
    return tienda || { numero: tiendaNumero, nombre: `Servicio ${tiendaNumero}` };
  };

  // Filtrar mensajes según el estado de showReadMessages
  const filteredMessages = showReadMessages 
    ? messages 
    : messages.filter(message => !message.leido);

  return (
    <div className="buzonAnonimoAdmin">
      <div className="titleContainer">
        <h1>Buzón Anónimo</h1>
        <div className="info">
          <p>Mensajes enviados anónimamente por los empleados para mejorar como empresa y equipo.</p>
        </div>
      </div>

      {/* Controles de filtro */}
      {!loadingTiendas && !error && messages.length > 0 && (
        <div className="filterContainer">
          <button 
            className={`filterButton ${showReadMessages ? 'active' : ''}`}
            onClick={() => setShowReadMessages(!showReadMessages)}
            title={showReadMessages ? "Ocultar mensajes leídos" : "Mostrar todos los mensajes"}
          >
            {showReadMessages ? (
              <>
                <VisibilityOffIcon />
                Ocultar leídos
              </>
            ) : (
              <>
                <VisibilityIcon />
                Mostrar todos
              </>
            )}
          </button>
          <span className="messageCount">
            {filteredMessages.length} de {messages.length} mensajes
          </span>
        </div>
      )}

      {/* Estado de carga o error */}
      {loadingTiendas ? (
        <div className="loadingContainer">
          <p>Cargando servicios asignados...</p>
        </div>
      ) : error ? (
        <div className="errorContainer">
          <p className="errorMessage">{error}</p>
        </div>
      ) : loading ? (
        <div className="loadingContainer">
          <p>Cargando mensajes...</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="emptyContainer">
          <p>No hay mensajes anónimos para mostrar en los servicios asignados.</p>
        </div>
      ) : (
        <div className="messagesContainer">
          {filteredMessages.map((message) => {
            // Encontrar información del servicio
            const servicioInfo = getServicioInfo(message.tiendaNumero);
            
            return (
              <div 
                key={message.id} 
                className={`messageCard ${message.leido ? 'read' : 'unread'}`}
              >
                <div className="messageHeader">
                  <div className="messageInfo">
                    <div className="messageDate">
                      {formatDate(message.fecha)}
                    </div>
                    {servicioInfo && (
                      <div className="messageTienda">
                        <span className="tiendaChip">
                          {servicioInfo.cliente && `${servicioInfo.cliente} - `}
                          {servicioInfo.nombre || `Servicio ${servicioInfo.numero}`}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="messageActions">
                    <button 
                      className="actionButton readButton"
                      onClick={() => toggleReadStatus(message.id, message.leido)}
                      title={message.leido ? "Marcar como no leído" : "Marcar como leído"}
                    >
                      {message.leido ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </button>
                    <button 
                      className="actionButton deleteButton"
                      onClick={() => handleDeleteClick(message.id)}
                      title="Eliminar mensaje"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
                
                <div 
                  className={`messageContent ${expandedMessage === message.id ? 'expanded' : ''}`}
                  onClick={() => toggleExpandMessage(message.id)}
                >
                  <p>{message.mensaje}</p>
                </div>
                
                <div className="messageFooter">
                  {message.categoria && (
                    <div className="messageCategory">
                      <span>Categoría: {message.categoria}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"¿Eliminar este mensaje?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Esta acción no se puede deshacer. El mensaje se eliminará permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={deleteMessage} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BuzonAnonimoAdmin;